"use strict"

import {
  SubscriptionCoupons,
  SubscriptionPlans,
  SubscriptionPlanPrices,
  SubscriptionTiers,
} from "../../model/domain/subscription"
import { ContractPlans } from "../../model/domain/contract"
import BreadcrumbService from "../../services/breadcrumb-service/breadcrumb.service"
import CacheService from "../../services/cache/cache.service"
import StaticContentService from "../../services/static/static-content.service"

export default class PaymentSuccessController {
  /**
   * @ngInject
   * @param {BreadcrumbService} BreadcrumbService
   * @param {CacheService} CacheService
   * @param {StaticContentService} StaticContentService
   */
  constructor(
    $log,
    $q,
    $stateParams,
    BreadcrumbService,
    CacheService,
    StaticContentService,
  ) {
    this.$log = $log
    this.$stateParams = $stateParams
    this.$q = $q

    /** @type {BreadcrumbService} */
    this._breadcrumbService = BreadcrumbService
    /** @type {CacheService} */
    this._cacheService = CacheService
    /** @type {StaticContentService} */
    this._staticContentService = StaticContentService

    this._loading = true
    this._nextCharge = undefined

    this.init()
  }

  init() {
    this._cacheService
      .getSubscriptions(true)
      .then((subscriptions) => {
        this._subscription = subscriptions.find(
          (subscription) => subscription.id === this.subscriptionId,
        )
        this._nextCharge = this.subscription.nextCharge
        this._loading = false
      })
      .catch((error) => {
        this._error = error
        this.$log.error(error.message)
      })
  }

  /**
   * @returns {string}
   */
  get subscriptionId() {
    return this.$stateParams.subscriptionId
  }

  /**
   * @returns {Subscription}
   */
  get subscription() {
    return this._subscription
  }

  get subscriptionPlan() {
    return this.subscription && this.subscription.subscriptionPlan
  }

  get subscriptionPlanName() {
    return SubscriptionTiers.find((tier) => tier.id === this.subscriptionPlan)
      ?.label
  }

  goBack() {
    this._breadcrumbService.goBack("root.account.home")
  }

  goHome() {
    this._breadcrumbService.go("root.account.home", {}, true)
  }

  goToGettingStartedWithPro() {
    this._staticContentService.goToGettingStartedTutorialVideo()
  }

  goToWatchTutorialVideo() {
    this._staticContentService.goToPortfolioStudentTutorialVideo()
  }

  get loading() {
    return this._loading
  }

  get error() {
    return this._error
  }

  get planPrice() {
    return SubscriptionTiers.find((tier) => tier.id === this.subscriptionPlan)
      ?.price
  }

  get annualOrMonthly() {
    return this.subscriptionPlan?.includes("annual") ? "Annual" : "Monthly"
  }

  get nextCharge() {
    return this._nextCharge
  }

  get coupon() {
    return this.subscription && this.subscription.coupon
  }

  get validCouponData() {
    if (
      this.coupon &&
      this.coupon.toLowerCase() ===
        SubscriptionCoupons.getCoupon(this.coupon)?.code
    ) {
      return SubscriptionCoupons.getCoupon(this.coupon)
    }
    return null
  }

  getDiscountMultiplier(percentage) {
    return parseFloat(percentage) / 100
  }

  get planDiscount() {
    const couponData = this.validCouponData

    if (couponData) {
      const discountRate = this.getDiscountMultiplier(
        couponData.discountPercentage,
      )
      const discountAmount = Number((this.planPrice * discountRate).toFixed(2))

      return `${couponData.discountPercentage} off (-$${discountAmount.toFixed(2)})`
    }

    return "--"
  }

  get planPriceAfterDiscount() {
    const couponData = this.validCouponData

    if (couponData) {
      const remainingPriceRate =
        1 - this.getDiscountMultiplier(couponData.discountPercentage)
      const discountedPrice = Number(
        (this.planPrice * remainingPriceRate).toFixed(2),
      )

      return discountedPrice.toFixed(2)
    }

    return this.planPrice.toFixed(2)
  }

  get totalPrice() {
    return `$${this.planPriceAfterDiscount}`
  }
}
